import { createEffect, createSignal, onCleanup, untrack, type Accessor } from 'solid-js';
import { PlayheadClock, PLAYHEAD_NOMINAL_INTERVAL_MS } from '../lib/playheadClock';
import { useVenue } from './VenueProvider';

/** The mm:ss text only ever changes once a second; resampling it four times a second is plenty. */
const CLOCK_TEXT_INTERVAL_MS = 250;

export interface SmoothPlayhead {
  /** Seconds, advanced on every animation frame while playing. Drives the progress bar. */
  position: Accessor<number>;
  /** The same clock, resampled at 4 Hz. Drives the mm:ss readout. */
  clockSeconds: Accessor<number>;
}

/**
 * Solid wiring for {@link PlayheadClock}: feed it every transport report and run it on animation frames
 * for as long as the room says a score is playing. The policy — what counts as drift, as a seek, or as
 * a loop wrap — lives in the clock, not here.
 *
 * **There is no report-cadence timer here, and there must not be one.** There used to be: reports
 * missing for four nominal intervals froze the bar where it stood, which is eight seconds on the cloud
 * route. On prod on 2026-09-18 the owner played a ten-second score with loop on; the daemon looped
 * silently, publishing nothing at the wrap, and the bar froze at 0:07 with the pause icon still lit —
 * the room was playing and the page said it had stopped. The reasoning behind the freeze was that a
 * frozen bar is a true statement about what we know, and it is exactly backwards: what we know is what
 * the ROOM last said, which was `playing`, and a bar that stops under a running score contradicts it.
 * A console cannot tell a room that pushes every two seconds from one that pushes only at a wrap, so a
 * cadence can never be evidence about the room. The room going away is a different fact, published on
 * a different channel, and the transport it leaves behind stops saying `playing` when it does.
 */
export function useSmoothPlayhead(durationSeconds: Accessor<number>): SmoothPlayhead {
  const venue = useVenue();
  const [position, setPosition] = createSignal(0);
  const [clockSeconds, setClockSeconds] = createSignal(0);
  const clock = new PlayheadClock(PLAYHEAD_NOMINAL_INTERVAL_MS.lan);
  let animationFrame = 0;
  let lastTextAt = 0;

  function intervalMs(): number {
    return venue.controlTransport() === 'cloud' ? PLAYHEAD_NOMINAL_INTERVAL_MS.cloud : PLAYHEAD_NOMINAL_INTERVAL_MS.lan;
  }

  function publish(now: number): void {
    const value = clock.at(now);
    setPosition(value);
    if (now - lastTextAt >= CLOCK_TEXT_INTERVAL_MS) {
      lastTextAt = now;
      setClockSeconds(value);
    }
  }

  function stopClock(): void {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  }

  function tick(now: number): void {
    animationFrame = 0;
    if (!clock.advancing) return;
    publish(now);
    animationFrame = requestAnimationFrame(tick);
  }

  createEffect(() => {
    const transport = venue.transport();
    const now = performance.now();
    clock.setNominalInterval(intervalMs());
    const fresh = clock.report({
      playing: transport?.state === 'playing',
      positionSeconds: transport?.positionSeconds,
      durationSeconds: untrack(durationSeconds),
      loop: transport?.loop === true,
      loopCount: transport?.loopCount,
      snapshotAt: transport?.snapshotAt,
    }, now);
    if (!fresh) return;

    publish(now);
    if (!clock.advancing) {
      stopClock();
      lastTextAt = 0;
      return;
    }
    if (!animationFrame) animationFrame = requestAnimationFrame(tick);
  });

  onCleanup(() => stopClock());

  return { position, clockSeconds };
}
